import { PanelFrame } from './panel-frame'

/**
 * `pages`: where you are, and what else is in here.
 *
 * "What can you actually do?" was the second thing everybody asked, and
 * the honest answer in this organisation is mostly "take you to a place
 * and do the thing there". So the panel is the current section's siblings,
 * read from the SAME manifest the agent navigates by
 * (`/nav-manifest.json`, generated from Connect's own nav config), which
 * means it cannot drift from what the agent believes exists.
 *
 * Read in the browser rather than through trunk on purpose: the manifest
 * is a static file the app already serves, so this panel costs one cached
 * fetch and works with no backend at all.
 */

import { pageFor, useNavManifest, type NavManifestItem } from '../use-nav-manifest'

/** Same section as the current route, which is what "in here" means. */
export function siblingsOf(
  items: readonly NavManifestItem[],
  pathname: string,
): { here: NavManifestItem | null; siblings: NavManifestItem[] } {
  const here = pageFor(items, pathname)
  if (!here) return { here: null, siblings: [] }
  const section = here.breadcrumb[0]
  return {
    here,
    siblings: items.filter(
      (item) => item.breadcrumb[0] === section && item.id !== here.id,
    ),
  }
}

export function PagesPanel({
  onClose,
  pathname,
  onGo,
}: {
  onClose: () => void
  pathname: string
  onGo: (path: string, label: string) => void
}) {
  const { items, failed } = useNavManifest()

  if (failed) {
    return (
      <PanelFrame id="pages" onClose={onClose}>
        <p className="text-sm text-muted-foreground">
          Could not read the page index.
        </p>
      </PanelFrame>
    )
  }
  if (!items) {
    return (
      <PanelFrame id="pages" onClose={onClose}>
        <p className="text-sm text-muted-foreground">Reading the page index…</p>
      </PanelFrame>
    )
  }

  const { here, siblings } = siblingsOf(items, pathname)

  return (
    <PanelFrame
      id="pages"
      onClose={onClose}
      note={here ? here.breadcrumb.join(' › ') : 'not a page it knows'}
    >
      {siblings.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {here
            ? 'Nothing else in this section.'
            : 'This page is not in the index the agent navigates by, so it will not offer to take you here.'}
        </p>
      ) : (
        <ul className="flex min-w-0 flex-col">
          {siblings.map((item) => {
            // The manifest keeps `{orgId}` and `{serial}` as placeholders;
            // the agent resolves them server-side. In the browser we
            // already know them from the route we are standing on.
            const resolved = item.path
              .replace('{orgId}', pathname.split('/')[1] ?? '')
              .replace('{serial}', pathname.split('/')[3] ?? '')
            const ready = !resolved.includes('{')
            return (
              <li key={item.id}>
                <button
                  type="button"
                  disabled={!ready}
                  onClick={() => {
                    onGo(resolved, item.breadcrumb.join(' › '))
                  }}
                  className="flex w-full min-w-0 flex-col items-start gap-0.5 rounded-lg px-2 py-2 text-left hover:bg-muted disabled:opacity-40"
                >
                  <span className="truncate text-sm text-foreground">
                    {item.label}
                  </span>
                  <span className="truncate text-[11px] text-muted-foreground">
                    {ready
                      ? item.breadcrumb.slice(0, -1).join(' › ')
                      : 'needs a venue'}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </PanelFrame>
  )
}
